import React, { Component, createRef, Fragment } from "react";
import PropTypes from "prop-types";
import * as R from "ramda";

import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";

import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import SelectField from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import ListItemText from "@mui/material/ListItemText";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import Switch from "@mui/material/Switch";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";

import { ICONS } from "~/constants/materials";
import { trackMaterialClick } from "~/libs/matomo";

import {
  attachmentListToMap,
  revmerge,
  isNewState,
  isPendingState,
  isUploadedState,
  filesToAttachments,
  getAttachments,
  removeAttachment,
  rejectByName,
  findByName,
  uploadAttachment,
  serialAttachmentUpload,
  getRevision
} from "~/libs/upload";

import DialogHeader from "~/components/Modal/ShowMaterialModal/DialogHeader";
import InputContextMenu from "~/components/InputContextMenu";
import AttachmentsTable from "../../AttachmentsTable";

const HiddenInput = styled("input")`
  display: none;
`;

const translationMap = {
  audio: "Audio-Datei",
  file: "Datei",
  game: "Internet-Spiel",
  image: "Bild",
  link: "Link",
  video: "Video",
  worksheet: "Arbeitsblatt"
};

class EditMaterialModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      material: props.material,
      attachments: {},
      materialTypeSelectOpen: false,
      renderTypeSelectOpen: false
    };

    this.onDelete = this.onDelete.bind(this);
    this.onSave = this.onSave.bind(this);
    this.onAbort = this.onAbort.bind(this);
  }

  uploadRef = createRef();

  componentDidMount() {
    this.fetchAttachments();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!R.equals(nextProps.material, this.state.material)) {
      this.setState({
        material: nextProps.material
      });
    }
  }

  fetchAttachments = () => getAttachments(this.props.material._id).then(attachments => this.setState({ attachments }));

  handleOnClick = name => (event, key, payload) => {
    this.setState(({ material }) => ({
      material: R.assoc(name, payload, material)
    }));
  };

  handleOnChange = name => event => {
    const { value } = event.target;
    this.setState(({ material }) => ({
      material: R.assoc(name, value, material)
    }));
  };

  handleDelete = () => {
    this.props.removeMaterial(this.state.material._id);
    this.props.hideModal();
  };

  saveChanges = () => {
    this.props.updateMaterial(this.state.material);
    this.props.switchModal("SHOW_MATERIAL");
  };

  openDialog = () => this.uploadRef.current.click();

  handleFiles = event => {
    const attachments = R.reduce(
      (acc, attachment) => R.assoc(attachment.name, attachment, acc),
      {},
      filesToAttachments(event.target.files)
    );

    this.setState(
      R.evolve({
        attachments: revmerge(attachments)
      })
    );
  };

  handleUpload = () => {
    this.setState(
      R.evolve({
        attachments: R.map(R.when(isNewState, R.assoc("status", "Laden...")))
      }),
      () => {
        const pendingAttachments = R.filter(isPendingState, this.state.attachments);

        const uploadTasks = R.map(
          attachment => rev => {
            if (R.isEmpty(rev)) {
              return getRevision(this.state.material._id).then(revision => {
                return uploadAttachment(this.state.material._id, revision, attachment);
              });
            }

            return uploadAttachment(this.state.material._id, rev, attachment);
          },
          Object.values(pendingAttachments)
        );

        serialAttachmentUpload(uploadTasks)
          .then(attachmentListToMap)
          .then(this.handleUploadResult)
          .catch(console.error);
      }
    );
  };

  handleUploadResult = result => {
    this.setState(
      R.evolve({
        attachments: R.compose(
          R.map(attachment => {
            const upload = R.propOr({}, attachment.name, result);

            if (R.has("error", upload)) {
              return {
                ...attachment,
                status: "Fehler"
              };
            }
            if (R.has("result", upload)) {
              return {
                ...attachment,
                status: "Hochgeladen"
              };
            }

            return attachment;
          }),
          Object.values
        )
      })
    );
  };

  handleAttachmentDelete = name => _ => {
    const attachment = findByName(name, this.state.attachments);
    let task = Promise.resolve();

    if (isUploadedState(attachment)) {
      task = removeAttachment(this.state.material._id, name);
    } else if (isNewState(attachment)) {
      task = Promise.resolve();
    }

    task
      .then(() =>
        this.setState(
          R.evolve({
            attachments: rejectByName(name)
          })
        )
      )
      .then(console.log)
      .catch(console.error);
  };

  onDelete() {
    this.handleDelete();
  }

  onSave() {
    this.saveChanges();
  }

  onAbort() {
    this.props.switchModal("SHOW_MATERIAL");
  }

  render() {
    const { material, page, kind } = this.props;

    const hasUnuploadedAttachments = R.reject(isPendingState, this.state.attachments).length > 0;

    trackMaterialClick({
      page,
      title: material.title
    });

    // If renderType is provided and not empty, then use it...else use the given materialType
    // We're going to use this to toggle the second select field
    // const type = R.ifElse(
    //   R.compose(R.isEmpty, R.propOr('', 'renderType')),
    //   R.prop('materialType'),
    //   R.prop('renderType')
    // )(material);

    const MaterialTypeIcon = R.propOr("link", this.state.material.materialType, ICONS);
    const RenderTypeIcon = R.propOr("link", this.state.material.renderType, ICONS);

    return (
      <>
        <InputContextMenu />
        <DialogHeader
          title={`Bearbeiten: ${this.state.material.title}`}
          isCloseableOnly={false}
          id={`${kind}-modal-title`}
        />
        <DialogContent>
          <TextField
            variant="standard"
            name="title"
            fullWidth
            label="Titel"
            placeholder="Gib einen Titel ein..."
            value={this.state.material.title}
            onChange={this.handleOnChange("title")}
          />

          <TextField
            variant="standard"
            name="linktext"
            fullWidth
            label="Linktext"
            placeholder="Gib einen Linktext ein..."
            value={this.state.material.linktext}
            onChange={this.handleOnChange("linktext")}
          />

          <TextField
            variant="standard"
            name="src"
            fullWidth
            label="Zielpfad"
            placeholder="Gib einen Zielpfad ein..."
            value={this.state.material.src}
            onChange={this.handleOnChange("src")}
          />

          <TextField
            variant="standard"
            name="description"
            multiLine
            rows={1}
            maxRows={4}
            fullWidth
            label="Beschreibung"
            placeholder="Gib eine Beschreibung ein..."
            value={this.state.material.description}
            onChange={this.handleOnChange("description")}
          />

          <div style={{ display: "flex" }}>
            <div style={{ flex: 1 }}>
              <FormControl variant="standard" style={{ width: "100%" }}>
                <InputLabel id="icon-label-edit">Dargestelltes Icon</InputLabel>
                <SelectField
                  variant="standard"
                  labelId="icon-label-edit"
                  onChange={(event, child) => {
                    this.handleOnClick("materialType")(null, null, event.target.value);
                  }}
                  onOpen={() => this.setState({ materialTypeSelectOpen: true })}
                  onClose={() => this.setState({ materialTypeSelectOpen: false })}
                  open={this.state.materialTypeSelectOpen}
                  value={this.state.material.materialType}
                  inputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <MaterialTypeIcon />
                      </InputAdornment>
                    )
                  }}
                  MenuProps={{
                    style: {
                      zIndex: 9999
                    }
                  }}
                >
                  {R.toPairs(ICONS).map(([name, Icon]) => (
                    <MenuItem key={name} value={name} selected={name === this.state.material.materialType}>
                      <ListItemText primary={translationMap[name]} />
                      <ListItemSecondaryAction>
                        <Icon />
                      </ListItemSecondaryAction>
                    </MenuItem>
                  ))}
                </SelectField>
              </FormControl>
            </div>

            <div style={{ flex: 1 }}>
              <FormControl variant="standard" style={{ width: "100%" }}>
                <InputLabel id="material-label-edit">Material Typ</InputLabel>
                <SelectField
                  variant="standard"
                  labelId="material-label-edit"
                  onChange={(event, child) => {
                    this.handleOnClick("renderType")(null, null, event.target.value);
                  }}
                  onOpen={() => this.setState({ renderTypeSelectOpen: true })}
                  onClose={() => this.setState({ renderTypeSelectOpen: false })}
                  open={this.state.renderTypeSelectOpen}
                  value={this.state.material.renderType || this.state.material.materialType}
                  inputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <RenderTypeIcon />
                      </InputAdornment>
                    )
                  }}
                  MenuProps={{
                    style: {
                      zIndex: 9999
                    }
                  }}
                >
                  {R.toPairs(ICONS).map(([name, Icon]) => (
                    <MenuItem key={name} value={name} selected={name === this.state.material.renderType}>
                      <ListItemText primary={translationMap[name]} />
                      <ListItemSecondaryAction>
                        <Icon />
                      </ListItemSecondaryAction>
                    </MenuItem>
                  ))}
                </SelectField>
              </FormControl>
            </div>
          </div>

          <div>
            <AttachmentsTable
              material={this.state.material}
              attachments={this.state.attachments}
              onDelete={this.handleAttachmentDelete}
              editable
            />
            <Button variant="text" color="primary" onClick={() => this.openDialog()}>
              Material hinzufügen
            </Button>
            <Button
              disabled={hasUnuploadedAttachments}
              variant="text"
              color="primary"
              onClick={() => this.handleUpload()}
            >
              Hochladen
            </Button>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            style={{ marginLeft: 0, marginRight: "auto" }}
            variant="text"
            color="secondary"
            onClick={this.onDelete}
          >
            <DeleteIcon />
            Material löschen
          </Button>
          <Button variant="text" color="primary" onClick={this.onSave}>
            Speichern
          </Button>
          <Button variant="text" color="primary" onClick={this.onAbort}>
            Abbrechen
          </Button>
        </DialogActions>
        <HiddenInput id="upload" type="file" ref={this.uploadRef} onChange={this.handleFiles} multiple />
      </>
    );
  }
}

EditMaterialModal.propTypes = {
  hideModal: PropTypes.func.isRequired,
  switchModal: PropTypes.func.isRequired,
  material: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  updateMaterial: PropTypes.func.isRequired,
  removeMaterial: PropTypes.func.isRequired,
  page: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default EditMaterialModal;
